/**
 * Accordion Component
 * Ported from afrodita-components - Simplified for template-project
 * Collapsible content panel matching Shapermint PDP style
 */

import { ReactNode, useState } from 'react';
import styled from '@emotion/styled';

export interface AccordionProps {
  variant: 'simple' | 'box';
  header: ReactNode;
  content: ReactNode | string;
  openIcon?: React.ComponentType<any>;
  closeIcon?: React.ComponentType<any>;
  defaultOpen?: boolean;
  innerHTML?: boolean;
  disabled?: boolean;
  titleColor?: string;
  onClick?: () => void;
  testId?: string;
  ssr?: boolean;
}

const AccordionContainer = styled.div<{ variant: string }>`
  background: transparent;
  width: 100%;
  border-bottom: ${({ variant, theme }) =>
    variant === 'simple' ? `1px solid ${theme.colors.border.color}` : 'none'};
  ${({ variant, theme }) =>
    variant === 'box' &&
    `
    border: 1px solid ${theme.colors.border.color};
    border-radius: ${theme.radius.regular};
    padding: ${theme.space.md};
  `}
`;

const AccordionButton = styled.button<{ variant: string; disabled: boolean }>`
  background: transparent;
  width: 100%;
  border: none;
  display: flex;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  justify-content: space-between;
  align-items: center;
  padding: ${({ variant, theme }) => (variant === 'simple' ? `${theme.space.lg} 0` : theme.space.md)};
  color: ${({ disabled, theme }) => (disabled ? theme.colors.text.tertiary : theme.colors.text.primary)};
  font-family: inherit;
  text-align: left;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
`;

const AccordionPanel = styled.div<{ isOpen: boolean; variant: string }>`
  width: 100%;
  background: transparent;
  border: none;
  max-height: ${({ isOpen }) => (isOpen ? '3000px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  padding-bottom: ${({ isOpen, variant, theme }) =>
    isOpen ? (variant === 'simple' ? theme.space.lg : theme.space.md) : '0'};
  
  /* Style for HTML content */
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
  
  p {
    margin: ${({ theme }) => `${theme.space.sm} 0`};
  }
  
  strong, b {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.typography.config.weight.semibold};
  }
  
  ul {
    margin: ${({ theme }) => `${theme.space.md} 0`};
    padding-left: ${({ theme }) => theme.space.xl};
    list-style-type: disc;
  }
  
  ol {
    margin: ${({ theme }) => `${theme.space.md} 0`};
    padding-left: ${({ theme }) => theme.space.xl};
  }
  
  li {
    margin: ${({ theme }) => `${theme.space.xs} 0`};
  }
  
  h1, h2, h3, h4, h5, h6 {
    color: ${({ theme }) => theme.colors.text.primary};
    margin: ${({ theme }) => `${theme.space.md} 0 ${theme.space.sm}`};
    }
  
  a {
    color: ${({ theme }) => theme.colors.primary.color};
    text-decoration: underline;
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  img, video {
    max-width: 100%;
    height: auto;
  }
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: ${({ theme }) => theme.space.sm};
`;

// Default Plus/Minus icons
const DefaultPlusIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const DefaultMinusIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export function Accordion({
  variant = 'simple',
  header,
  content,
  openIcon: OpenIcon,
  closeIcon: CloseIcon,
  defaultOpen = false,
  innerHTML = false,
  disabled = false,
  titleColor,
  onClick,
  testId,
  ssr = false,
}: AccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    onClick?.();
  };

  const shouldShow = isOpen || ssr;
  const ControlIcon = isOpen ? (CloseIcon || DefaultMinusIcon) : (OpenIcon || DefaultPlusIcon);

  return (
    <AccordionContainer data-testid={testId} variant={variant}>
      <AccordionButton
      variant={variant}
        disabled={disabled}
        onClick={handleToggle}
        style={titleColor ? { color: titleColor } : undefined}
        aria-expanded={isOpen}
      >
        <div style={{ flex: 1 }}>{header}</div>
        <IconWrapper>
          <ControlIcon title={isOpen ? 'close icon' : 'open icon'} />
        </IconWrapper>
      </AccordionButton>
      {shouldShow && (
        <AccordionPanel isOpen={isOpen} variant={variant}>
          {innerHTML && typeof content === 'string' ? (
            <div dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            content
          )}
        </AccordionPanel>
      )}
    </AccordionContainer>
  );
}

